import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useProject } from '../contexts/ProjectContext';
import { getApiUrl } from '../utils/api';
import { authDownload, authFetch } from '../utils/authFetch';
import { sanitizeFileNameSegment } from '../utils/stringSanitization';
import { calculatePixelsPerFrame } from '../components/VideoSettings';
import { formatFramesToClock } from '../utils/cardTiming';
import { Download } from '../components/animate-ui/icons/download';
import { Trash2 } from '../components/animate-ui/icons/trash-2';
import './Renders.css';
import { SkeletonTable } from '../components/Skeleton';

interface RendersProps {
  renderControl?: React.ReactNode;
}

interface RenderItem {
  version: number;
  status: string;
  created_at: string;
  file_name: string;
  video_settings: {
    background_color?: string;
    fps_num?: number;
    resolution?: string;
    transparent?: boolean;
    video_time?: number;
    video_time_frames?: number;
    aspect_ratio?: string;
    video_format?: string;
  };
}

const RENDERS_REFRESH_INTERVAL_MS = 10000;

const listRenders = async (projectId: string): Promise<RenderItem[]> => {
  const response = await authFetch(`${getApiUrl()}/list-renders?projectId=${projectId}`);
  if (!response.ok) throw new Error('Failed to fetch renders');
  const data = await response.json();
  return data.renders || [];
};

const getRender = async (
  projectId: string,
  version: number,
  fileName: string,
  projectName: string,
  videoFormat?: string,
  resolution?: string,
  aspectRatio?: string
): Promise<void> => {
  const extension = fileName.includes('.') ? fileName.split('.').pop() || 'mp4' : 'mp4';
  const downloadFileName = [
    sanitizeFileNameSegment(projectName, { fallback: 'project', lowercase: false }),
    sanitizeFileNameSegment(videoFormat, { fallback: extension, lowercase: false }),
    sanitizeFileNameSegment(resolution, { fallback: 'res', lowercase: false }),
    sanitizeFileNameSegment(aspectRatio, { fallback: 'aspect' }),
    `v${version}`,
    new Date().toISOString().split('T')[0],
  ].join('_') + `.${extension}`;
  const downloadUrl = `${getApiUrl()}/get-render?projectId=${encodeURIComponent(
    projectId
  )}&version=${encodeURIComponent(String(version))}&downloadName=${encodeURIComponent(downloadFileName)}`;

  await authDownload(downloadUrl, downloadFileName);
};

const deleteRender = async (projectId: string, version: number): Promise<void> => {
  const response = await authFetch(`${getApiUrl()}/delete-render`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ projectId, version }),
  });
  if (!response.ok) throw new Error('Failed to delete render');
};

const cancelRender = async (projectId: string, version: number): Promise<void> => {
  const response = await authFetch(`${getApiUrl()}/cancel-render`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ projectId, version }),
  });
  if (!response.ok) throw new Error('Failed to cancel render');
};

const formatDateTime = (dateString: string, locale: string): string => {
  const utcDate = new Date(dateString + 'Z');
  const localDate = toZonedTime(utcDate, Intl.DateTimeFormat().resolvedOptions().timeZone);
  return format(localDate, locale === 'pl' ? 'dd.MM.yyyy - HH:mm:ss' : 'dd/MM/yyyy - HH:mm:ss');
};

const getResolutionLabel = (videoSettings?: RenderItem['video_settings']): string => {
  if (!videoSettings) return '';
  if (videoSettings.resolution?.trim()) return videoSettings.resolution;

  const rawWidth = (videoSettings as Record<string, unknown>).video_width ?? (videoSettings as Record<string, unknown>).width;
  const width = typeof rawWidth === 'number' ? rawWidth : Number(rawWidth);
  if (!Number.isFinite(width)) return '';
  if (width >= 4000) return '4K';
  if (width >= 3800) return 'UHD';
  if (width >= 2000) return '2K';
  if (width >= 1200) return 'HD';
  return `${Math.round(width)}px`;
};

const getDurationFrames = (params: any): number | null => {
  const vs = params.data?.video_settings;
  const durationFrames = Number(vs?.video_time_frames);
  if (Number.isFinite(durationFrames)) return durationFrames;

  const videoTime = Number(vs?.video_time);
  const framesPerSecond = Number(vs?.fps_num);
  return Number.isFinite(videoTime) && Number.isFinite(framesPerSecond)
    ? Math.round(videoTime * framesPerSecond)
    : null;
};

const formatDuration = (params: any): string => {
  const durationFrames = Number(params.value);
  const framesPerSecond = Number(params.data?.video_settings?.fps_num);
  if (!Number.isFinite(durationFrames) || !Number.isFinite(framesPerSecond) || durationFrames < 0 || framesPerSecond <= 0) {
    return '';
  }
  return formatFramesToClock(Math.round(durationFrames), framesPerSecond);
};

const RefreshCountdown = memo(({ nextRefreshAt, refreshing }: { nextRefreshAt: number; refreshing: boolean }) => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(interval);
  }, []);

  const remainingMs = Math.max(0, nextRefreshAt - now);
  const remainingSeconds = Math.ceil(remainingMs / 1000);
  const elapsedPercent = Math.min(100, Math.max(0, ((RENDERS_REFRESH_INTERVAL_MS - remainingMs) / RENDERS_REFRESH_INTERVAL_MS) * 100));

  return (
    <Box
      className="renders-refresh-ring"
      aria-label={refreshing ? String(t`Updating renders`) : String(t`Next renders update in ${remainingSeconds} seconds`)}
    >
      <CircularProgress variant="determinate" value={refreshing ? 100 : elapsedPercent} size={38} thickness={4} />
      <span>{refreshing ? '...' : `${remainingSeconds}s`}</span>
    </Box>
  );
});

const Renders: React.FC<RendersProps> = ({ renderControl }) => {
  const { selectedProject } = useProject();
  const canEdit = selectedProject?.permission_tier === 1 || selectedProject?.permission_tier === 2;
  const { i18n } = useLingui();
  const locale = i18n.locale;
  const isRtl = locale === 'he';
  const [renders, setRenders] = useState<RenderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextRefreshAt, setNextRefreshAt] = useState(() => Date.now() + RENDERS_REFRESH_INTERVAL_MS);
  const [downloadingVersions, setDownloadingVersions] = useState<Set<number>>(() => new Set());
  const downloadingVersionsRef = useRef<Set<number>>(new Set());
  const [cancelTarget, setCancelTarget] = useState<{ projectId: string; version: number } | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const keepRenderingButtonRef = useRef<HTMLButtonElement>(null);
  const keepRenderButtonRef = useRef<HTMLButtonElement>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ projectId: string; version: number } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<RenderItem | null>(null);
  const currentProjectRef = useRef(selectedProject?.id);
  currentProjectRef.current = selectedProject?.id;
  const deletingRef = useRef(false);

  useEffect(() => {
    setCancelTarget(null);
    setCancelError(null);
    setDeleteTarget(null);
    setDeleteError(null);
    setDownloadError(null);
  }, [selectedProject?.id]);

  const StatusCellRenderer = useCallback((props: { value: string }) => {
    const statusNum = parseInt(props.value, 10);
    if (statusNum === -2) return <span lang={locale} className="render-status render-status-pending">{t`Cancelling...`}</span>;
    if (statusNum === -1) return <span lang={locale} className="render-status render-status-failed">{t`Failed`}</span>;
    if (statusNum === 100) return <span lang={locale} className="render-status render-status-complete">{t`Complete`}</span>;
    if (statusNum === 0) return <span lang={locale} className="render-status render-status-pending">{t`Pending`}</span>;
    return <span lang={locale} className="render-status render-status-pending">{statusNum}%</span>;
  }, [locale]);

  const fetchRenders = useCallback(async () => {
    if (!selectedProject) {
      setError(String(t`No project selected`));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setRenders(await listRenders(selectedProject.id));
    } catch (err) {
      console.error('Error fetching renders:', err);
      setError(String(t`Failed to load renders`));
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  useEffect(() => {
    const refreshRenders = () => {
      setNextRefreshAt(Date.now() + RENDERS_REFRESH_INTERVAL_MS);
      fetchRenders();
    };

    refreshRenders();
    const interval = setInterval(refreshRenders, RENDERS_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchRenders]);

  const handleDownload = useCallback(async (renderItem: RenderItem) => {
    if (!selectedProject) return;
    const { version, file_name: fileName, video_settings: videoSettings } = renderItem;

    if (downloadingVersionsRef.current.has(version)) return;
    downloadingVersionsRef.current.add(version);
    setDownloadError(null);
    setDownloadingVersions((previous) => new Set(previous).add(version));

    try {
      await getRender(
        selectedProject.id,
        version,
        fileName,
        selectedProject.name,
        videoSettings?.video_format,
        getResolutionLabel(videoSettings),
        videoSettings?.aspect_ratio
      );
    } catch (err) {
      console.error('Error downloading render:', err);
      if (currentProjectRef.current === selectedProject.id) setDownloadError(renderItem);
    } finally {
      downloadingVersionsRef.current.delete(version);
      setDownloadingVersions((previous) => {
        const next = new Set(previous);
        next.delete(version);
        return next;
      });
    }
  }, [selectedProject]);

  const handleDelete = async () => {
    if (!deleteTarget || deletingRef.current) return;
    const target = deleteTarget;
    deletingRef.current = true;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteRender(target.projectId, target.version);
      if (currentProjectRef.current === target.projectId) {
        setDeleteTarget(null);
        await fetchRenders();
      }
    } catch (err) {
      console.error('Error deleting render:', err);
      if (currentProjectRef.current === target.projectId) setDeleteError(String(t`Could not delete this render. Try again.`));
    } finally {
      deletingRef.current = false;
      setDeleting(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelTarget || cancelling) return;
    setCancelling(true);
    setCancelError(null);
    try {
      await cancelRender(cancelTarget.projectId, cancelTarget.version);
      setCancelTarget(null);
      await fetchRenders();
    } catch (err) {
      setCancelError(String(t`Failed to cancel render. It may have already finished. Refresh and try again.`));
      await fetchRenders();
    } finally {
      setCancelling(false);
    }
  };

  const ActionsCellRenderer = useCallback((props: { data: RenderItem }) => {
    const renderItem = props.data;
    const status = parseInt(renderItem.status, 10);
    const isComplete = status === 100;
    const isActive = status >= 0 && status < 100;
    const isCancelling = status === -2;
    const isDownloading = downloadingVersions.has(renderItem.version);
    const isBusy = isActive || isCancelling || isDownloading;
    const downloadLabel = isDownloading ? t`Downloading...` : isCancelling ? t`Cancelling...` : isActive ? (status === 0 ? t`Pending` : t`Rendering...`) : t`Download`;
    const canDownload = isComplete && !isDownloading;

    return (
      <div className="render-row-actions">
        <button
          className="render-action-button render-download-button"
          onClick={() => handleDownload(renderItem)}
          disabled={!canDownload}
          aria-busy={isBusy}
          aria-label={String(downloadLabel)}
          title={String(downloadLabel)}
        >
          {isBusy ? (
            <CircularProgress size={18} thickness={4} color="inherit" aria-hidden="true" />
          ) : (
            <Download animateOnHover size={18} aria-hidden="true" />
          )}
        </button>
        {(isActive || isCancelling) && (
          <button
            className="render-action-button render-delete-button"
            onClick={() => {
              if (!selectedProject) return;
              setCancelError(null);
              setCancelTarget({ projectId: selectedProject.id, version: renderItem.version });
            }}
            disabled={isCancelling}
            aria-label={String(isCancelling ? t`Cancelling...` : t`Cancel render`)}
            title={String(isCancelling ? t`Cancelling...` : t`Cancel render`)}
          >
            <CancelOutlinedIcon sx={{ fontSize: 18 }} aria-hidden="true" />
          </button>
        )}
        {canEdit && !isActive && !isCancelling && (
          <button
            className="render-action-button render-delete-button"
            onClick={() => {
              if (!selectedProject) return;
              setDeleteError(null);
              setDeleteTarget({ projectId: selectedProject.id, version: renderItem.version });
            }}
            aria-label={String(t`Delete`)}
            title={String(t`Delete`)}
          >
            <Trash2 animateOnHover size={18} aria-hidden="true" />
          </button>
        )}
      </div>
    );
  }, [canEdit, downloadingVersions, handleDownload, selectedProject]);

  const columnDefs = useMemo<ColDef[]>(() => {
    const flex = (baseWidth: number) => ({ flex: baseWidth, minWidth: 65 });
    const definitions: ColDef[] = [
    { field: 'version', headerName: String(t`Version`), filter: true, ...flex(80) },
    { field: 'status', headerName: String(t`Status`), filter: true, cellRenderer: StatusCellRenderer, ...flex(100) },
    {
      field: 'video_settings.video_format',
      headerName: String(t`Video Format`),
      filter: true,
      valueGetter: (params: any) => params.data?.video_settings?.video_format,
      ...flex(120),
    },
    {
      field: 'video_settings.fps_num',
      headerName: String(t`FPS`),
      filter: true,
      valueGetter: (params: any) => params.data?.video_settings?.fps_num,
      ...flex(80),
    },
    {
      field: 'video_settings.resolution',
      headerName: String(t`Resolution`),
      filter: true,
      valueGetter: (params: any) => getResolutionLabel(params.data?.video_settings),
      ...flex(110),
    },
    {
      field: 'video_settings.video_time_frames',
      headerName: String(t`Duration`),
      filter: true,
      valueGetter: getDurationFrames,
      valueFormatter: formatDuration,
      ...flex(130),
    },
    {
      headerName: String(t`Px/Frame`),
      filter: true,
      valueGetter: (params: any) => {
        const vs = params.data?.video_settings;
        if (!vs?.resolution || !vs?.aspect_ratio) return null;
        return calculatePixelsPerFrame({
          resolution: vs.resolution,
          aspect_ratio: vs.aspect_ratio,
          fps_num: vs.fps_num,
          video_time: vs.video_time,
          video_time_frames: vs.video_time_frames,
        });
      },
      ...flex(95),
    },
    {
      field: 'video_settings.aspect_ratio',
      headerName: String(t`Aspect Ratio`),
      filter: true,
      valueGetter: (params: any) => params.data?.video_settings?.aspect_ratio,
      ...flex(120),
    },
    {
      field: 'video_settings.background_color',
      headerName: String(t`BG Color`),
      filter: true,
      valueGetter: (params: any) => params.data?.video_settings?.background_color,
      ...flex(105),
    },
    {
      field: 'video_settings.transparent',
      headerName: String(t`Transparent`),
      filter: true,
      valueGetter: (params: any) => params.data?.video_settings?.transparent,
      valueFormatter: (params: any) => {
        if (typeof params.value !== 'boolean') return '';
        return params.value ? String(t`Yes`) : String(t`No`);
      },
      ...flex(115),
    },
    {
      field: 'created_at',
      headerName: String(t`Created At`),
      headerClass: 'render-created-at-header',
      cellClass: 'render-created-at-cell',
      filter: true,
      valueFormatter: (params: any) => params.value ? formatDateTime(params.value, locale) : '',
      ...flex(190),
      minWidth: 170,
    },
    {
      field: 'actions',
      valueGetter: (params) => params.data?.status,
      headerName: String(t`Actions`),
      cellRenderer: ActionsCellRenderer,
      sortable: false,
      filter: false,
      ...flex(195),
      minWidth: 195,
      pinned: isRtl ? 'left' : 'right',
    },
  ];
    // AG Grid mirrors center columns when enableRtl is true. Keep the logical
    // order here so it is displayed in the opposite visual direction for RTL.
    return definitions;
  }, [ActionsCellRenderer, StatusCellRenderer, isRtl, locale]);

  if (!selectedProject) return null;

  return (
    <div className="renders-page">
      <Dialog className="render-cancel-dialog" PaperProps={{ role: 'alertdialog' }} open={deleteTarget !== null}
        TransitionProps={{ onEntered: () => keepRenderButtonRef.current?.focus() }}
        onClose={() => { if (!deletingRef.current) setDeleteTarget(null); }}
        aria-labelledby="delete-render-title" aria-describedby="delete-render-description" dir={isRtl ? 'rtl' : 'ltr'}>
        <DialogTitle id="delete-render-title" className="render-cancel-title">{t`Delete render`}</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-render-description">{t`Delete render version ${deleteTarget?.version ?? ''}? This cannot be undone.`}</DialogContentText>
          {deleteError && <p className="render-cancel-error" role="alert">{deleteError}</p>}
        </DialogContent>
        <DialogActions>
          <Button className="render-cancel-keep" ref={keepRenderButtonRef} onClick={() => setDeleteTarget(null)} disabled={deleting}>{t`Cancel`}</Button>
          <Button className="render-cancel-confirm" color="error" onClick={handleDelete} disabled={deleting}>{deleting ? t`Deleting…` : t`Delete`}</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        className="render-cancel-dialog"
        PaperProps={{ role: 'alertdialog' }}
        TransitionProps={{ onEntered: () => keepRenderingButtonRef.current?.focus() }}
        open={cancelTarget !== null}
        onClose={() => { if (!cancelling) setCancelTarget(null); }}
        aria-labelledby="cancel-render-title"
        aria-describedby="cancel-render-description"
        dir={isRtl ? 'rtl' : 'ltr'}
      >
        <DialogTitle id="cancel-render-title" className="render-cancel-title">
          <span className="render-cancel-symbol" aria-hidden="true">
            <CancelOutlinedIcon />
          </span>
          <span>{t`Cancel render`}</span>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="cancel-render-description">
            {t`Stop render version ${cancelTarget?.version ?? ''}? The render and its partial output will be removed.`}
          </DialogContentText>
          {cancelError && <p className="render-cancel-error" role="alert">{cancelError}</p>}
        </DialogContent>
        <DialogActions>
          <Button ref={keepRenderingButtonRef} className="render-cancel-keep" autoFocus onClick={() => setCancelTarget(null)} disabled={cancelling}>{t`Keep rendering`}</Button>
          <Button className="render-cancel-confirm" onClick={handleCancel} color="error" disabled={cancelling}>
            {cancelling ? t`Cancelling...` : t`Cancel render`}
          </Button>
        </DialogActions>
      </Dialog>
      <div className="renders-title-row">
        <h1>{t`Renders`} - {selectedProject.name}</h1>
        <RefreshCountdown nextRefreshAt={nextRefreshAt} refreshing={loading && renders.length > 0} />
      </div>
      {renderControl && <div className="renders-action-bar">{renderControl}</div>}

      {error && (
        <div className="workspace-action-error" role="alert">
          {error} <button type="button" disabled={loading} onClick={() => void fetchRenders()}>{t`Retry`}</button>
        </div>
      )}
      {downloadError && <div className="workspace-action-error" role="alert">{t`Could not download version ${downloadError.version}.`} <button type="button" onClick={() => void handleDownload(downloadError)}>{t`Retry`}</button></div>}
      {loading && renders.length === 0 ? (
        <div aria-busy="true" aria-label={String(t`Loading renders...`)} role="status">
          <span className="sr-only">{t`Loading renders...`}</span>
          <SkeletonTable columns={8} rows={10} />
        </div>
      ) : !error && renders.length === 0 ? (
        <p>{t`No renders generated yet`}</p>
      ) : (
        <div className="ag-theme-alpine" style={{ height: '600px', width: '100%' }}>
          <AgGridReact
            key={isRtl ? 'rtl' : 'ltr'}
            enableRtl={isRtl}
            rowData={renders}
            getRowId={({ data }) => String(data.version)}
            columnDefs={columnDefs}
            maintainColumnOrder
            defaultColDef={{
              resizable: false,
              sortable: false,
              filter: true,
              wrapHeaderText: true,
              autoHeaderHeight: true,
            }}
            animateRows={false}
            suppressCellFocus
            theme="legacy"
          />
        </div>
      )}
    </div>
  );
};

export default Renders;
